import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Evento beforeinstallprompt do PWA (não está em tipos do TypeScript). */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWA_DISMISS_KEY = 'autolac_pwa_install_dismissed';

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private readonly canInstall$ = new BehaviorSubject<boolean>(false);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeinstallprompt', (e: Event) => {
        e.preventDefault();
        this.deferredPrompt = e as BeforeInstallPromptEvent;
        if (!this.wasDismissed()) {
          this.canInstall$.next(true);
        }
      });
      window.addEventListener('appinstalled', () => {
        this.deferredPrompt = null;
        this.canInstall$.next(false);
      });
    }
  }

  get canInstall(): Observable<boolean> {
    return this.canInstall$.asObservable();
  }

  get installAvailable(): boolean {
    return this.canInstall$.value;
  }

  /** Mostra o prompt nativo de instalação. Só funciona após beforeinstallprompt. */
  async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) return false;
    await this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      this.canInstall$.next(false);
      return true;
    }
    return false;
  }

  /** Esconde o banner por 7 dias (evita insistência). */
  dismissBanner(): void {
    const until = Date.now() + 7 * 24 * 60 * 60 * 1000;
    try {
      localStorage.setItem(PWA_DISMISS_KEY, String(until));
    } catch {
      // ignore
    }
    this.canInstall$.next(false);
  }

  wasDismissed(): boolean {
    try {
      const until = localStorage.getItem(PWA_DISMISS_KEY);
      if (!until) return false;
      return Date.now() < parseInt(until, 10);
    } catch {
      return false;
    }
  }
}
